import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AUTH_API_CONFIG } from '@fy-web-2/data-access-auth';
import { AuthStore } from '@fy-web-2/state-auth';

/**
 * Attaches `Authorization: Bearer <accessToken>` to outbound requests that
 * target the configured API domain (`AUTH_API_CONFIG.baseUrl`) or are
 * same-origin relative paths.
 *
 * The OAuth `/oauth/token` endpoint is always skipped — token exchanges must
 * not carry stale Bearer headers. Cross-origin requests (e.g. third-party
 * services like analytics, maps, CDNs) are also skipped to avoid leaking
 * tokens.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('/oauth/token')) {
    return next(req);
  }

  if (!isInDomain(req.url, inject(AUTH_API_CONFIG).baseUrl)) {
    return next(req);
  }

  const token = inject(AuthStore).accessToken();
  if (!token) {
    return next(req);
  }

  return next(
    req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    }),
  );
};

function isInDomain(reqUrl: string, baseUrl: string): boolean {
  // Relative paths are same-origin → always attach (covers proxy-served dev).
  if (!/^https?:\/\//i.test(reqUrl)) {
    return true;
  }

  // No absolute baseUrl configured (e.g. dev with proxy): only relative URLs
  // qualify, so absolute requests are external and must not receive the token.
  if (!baseUrl || !/^https?:\/\//i.test(baseUrl)) {
    return false;
  }

  try {
    return new URL(reqUrl).origin === new URL(baseUrl).origin;
  } catch {
    return false;
  }
}
